import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { useSpring, animated } from 'react-spring';
import FundFollowButton from './fund-follow-button';
import useFundStateStore from '../common/fund-state-hook';
import useNavigationStateStore from '../../shared/navigation/navigation-state-hook';
import IconedButton from '../../shared/iconed-button';
import EnquiryContext from '../../shared/enquiry/context';
import NavMenu from '../../shared/navigation/nav-menu';
import { recordStatisticsEvent } from '../../../services/statistics-service';

const height = 52;
const Container = styled(animated.div)`
  z-index: 20;
  width: 100vw;
  height: ${height}px;
  background: white;
  position: fixed;
  top: 66px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.1);

  &.new-nav {
    top: 48px;

    ${props => props.theme.isXLarge && `
      top: 104px;
    `};
  }
`;

const Content = styled.div`
  max-width: 1200px;
  padding: 0;
  margin: 0 auto;

  ${props => props.theme.isLarge && `
    width: 85%;
  `};
  ${props => props.theme.isXLarge && `
    width: 953px;
  `};
  ${props => props.theme.isXXLarge && `
    width: 1200px;
  `};
  display: flex;
  align-items: center;
`;

const Buttons = styled.div`
  max-width: 186px;
  align-self: flex-end;
  display: flex;
  justify-content: space-between;

  & > button {
    margin: 0;
    margin-left: 8px;
    height: 36px;
    outline: none;
  }

  &:not(.discreet) > button:not([name="fund-follow-button"]) {
    width: 36px;
    padding: 6px;
    background-color: ##273147;

    > span:not(.icon-lw) {
      display: none;
    }
  }

  &&.discreet {
    > button[name="fund-follow-button"] {
      display: none;
    }

    > button:not([name="fund-follow-button"]) {
      background-color: #3c4966;
      width: 134px;
      > span.icon-lw-contact {
        padding: 3px;
      }
      & > span:not(.icon-lw) {
        font-size: 14px;
      }
    }
  }

  &&&& > button:not([name="fund-follow-button"]) {
    color: white;

    > span.icon-lw-contact {
      background-color: ${props => props.theme.bodyColor};
    }
  }
`;

export default function StickyHeader({
  fund, showContactButton, yOffset, newNavEnabled,
}) {
  const { dispatch } = React.useContext(EnquiryContext);
  const [discreet, setDiscreet] = React.useState(fund.isFollowedByCurrentUser);
  const following = useFundStateStore(state => state.followed);
  const showFeaturedWires = useNavigationStateStore(state => state.hasFeaturedWires);
  const showQandA = useNavigationStateStore(state => state.hasQandA);
  const showPerformanceUpdates = useFundStateStore(state => state.hasPerformanceUpdates);
  const showInvest = useFundStateStore(state => state.hasInvest);
  const showFee = useFundStateStore(state => state.hasFeesPricing);
  const navItems = [];

  if (showFeaturedWires) {
    navItems.push({ title: 'featured wires', sectionName: 'featured-wires' });
  }
  navItems.push({ title: 'information', sectionName: 'fund-information' });
  if (showPerformanceUpdates) {
    navItems.push({ title: 'performance', sectionName: 'fund-performance' });
  }
  if (showFee) {
    navItems.push({ title: 'fees & pricing', sectionName: 'fund-fees' });
  }
  if (showQandA) {
    navItems.push({ title: 'manager q & a', sectionName: 'q-and-a' });
  }
  if (showInvest) {
    navItems.push({ title: 'invest', sectionName: 'invest' });
  }

  const navMenuProps = {
    navItems,
    id: fund.id,
    statisticable: 'fund',
    url: `/fund/${fund.slug}`,
  };

  const isVisible = yOffset > 180;

  const [styles, setStyles] = useSpring(() => ({ transform: `translateY(${-height}px)` }));
  setStyles({
    transform: `translateY(${isVisible ? 0 : -height}px)`,
    config: { tension: isVisible ? 170 : 350, clamp: true },
  });

  const contact = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'sticky_header_contact_fund',
      statisticable: 'fund',
      id: fund.id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      dispatch({ type: 'showEnquiryForm', payload: { scroll: true, offset: -200 } });
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  React.useEffect(() => {
    setDiscreet(following && fund.isFollowedByCurrentUser);
  }, [following, fund.isFollowedByCurrentUser]);

  return (
    <Container style={styles} className={newNavEnabled ? 'new-nav' : ''}>
      <Content>
        <NavMenu {...navMenuProps} />
        <Buttons className={classNames({ discreet })}>
          <FundFollowButton fund={fund} />
          {!!showContactButton && (
            <IconedButton type="button" className="btn" onClick={contact}>
              <span className="icon-lw icon-lw-contact" />
              <span>contact</span>
            </IconedButton>
          )}
        </Buttons>
      </Content>
    </Container>
  );
}

StickyHeader.propTypes = {
  fund: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string.isRequired,
    isFollowedByCurrentUser: PropTypes.bool,
  }).isRequired,
  showContactButton: PropTypes.bool.isRequired,
  yOffset: PropTypes.number.isRequired,
  newNavEnabled: PropTypes.bool.isRequired,
};
